import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import api, { ApiError } from "./api";
import { useAuth } from "../hooks/auth";

/** Module-level, so navigating away and back does not refetch. */
const cache = new Map<string, { data: unknown; at: number }>();

export const cachedAt = (url?: string | null): number | null =>
  url ? cache.get(url)?.at ?? null : null;

interface CachedState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}

/**
 * `useGet` with a time-bounded cache. Separate from it because that hook is
 * used across every page and this behaviour should not be imposed on them.
 */
export const useCachedGet = <T>(url?: string | null, cacheMs = 5 * 60_000) => {
  const { getToken } = useAuth();

  const readFresh = useCallback((): T | null => {
    if (!url) return null;
    const hit = cache.get(url);
    return hit && Date.now() - hit.at < cacheMs ? (hit.data as T) : null;
  }, [url, cacheMs]);

  const [state, setState] = useState<CachedState<T>>(() => {
    const fresh = readFresh();
    return { data: fresh, loading: !!url && !fresh, error: null };
  });
  const [fetchedAt, setFetchedAt] = useState<number | null>(() => cachedAt(url));

  const load = useCallback(
    async (force = false) => {
      if (!url) {
        setState({ data: null, loading: false, error: null });
        return null;
      }
      if (!force) {
        const fresh = readFresh();
        if (fresh !== null) {
          setState({ data: fresh, loading: false, error: null });
          setFetchedAt(cachedAt(url));
          return fresh;
        }
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const token = await getToken();
        const data = await api.get<T>(url, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        cache.set(url, { data, at: Date.now() });
        setState({ data, loading: false, error: null });
        setFetchedAt(Date.now());
        return data;
      } catch (error) {
        const apiError: ApiError = {
          message: (error as AxiosError).message,
          status: (error as AxiosError).response?.status || 500,
          data: (error as AxiosError).response?.data,
        };
        setState((prev) => ({ ...prev, loading: false, error: apiError }));
        return null;
      }
    },
    [url, getToken, readFresh]
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  return { ...state, fetchedAt, reload: () => load(true) };
};
